import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import UniversalHeader from './UniversalHeader';
import GameSelector from './MentalMath/GameSelector';
import SpeedArithmeticGame from './MentalMath/SpeedArithmeticGame';
import PatternRecognitionGame from './MentalMath/PatternRecognitionGame';
import MathBattleGame from './MentalMath/MathBattleGame';
import AbacusGame from './MentalMath/AbacusGame';
import GameResults from './MentalMath/GameResults';
// import GameLeaderboard from './MentalMath/GameLeaderboard'; // Removed for now

const MentalMathSection = ({ onBackToDashboard }) => {
  const { user, userProfile } = useAuth();
  const [currentView, setCurrentView] = useState('selector'); // 'selector', 'game', 'results'
  const [selectedGame, setSelectedGame] = useState(null);
  const [gameSession, setGameSession] = useState(null);
  const [gameResults, setGameResults] = useState(null);
  const [availableGames, setAvailableGames] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch available games on component mount
  useEffect(() => {
    fetchAvailableGames();
  }, []);

  const getDefaultGames = () => [
    {
      id: 'speed_arithmetic',
      name: 'Speed Arithmetic',
      game_type: 'speed_arithmetic',
      description: '2-minute math challenge! Solve as many problems as you can.',
      difficulty_levels: {
        easy: { name: 'Easy (1-12)', range: [1, 12] },
        medium: { name: 'Medium (1-50)', range: [1, 50] },
        hard: { name: 'Hard (1-100)', range: [1, 100] }
      },
      is_premium: false
    },
    {
      id: 'pattern_recognition',
      name: 'Number Patterns',
      game_type: 'pattern_recognition', 
      description: 'Complete number sequences and find patterns.',
      difficulty_levels: {
        easy: { name: 'Basic Patterns', complexity: 1 },
        medium: { name: 'Intermediate Patterns', complexity: 2 },
        hard: { name: 'Advanced Patterns', complexity: 3 }
      },
      is_premium: false
    },
    {
      id: 'math_battle',
      name: 'Math Battle',
      game_type: 'math_battle',
      description: 'Fight monsters by solving math problems! RPG-style adventure.',
      difficulty_levels: {
        easy: { name: 'Apprentice (1-12)', range: [1, 12] },
        medium: { name: 'Warrior (1-25)', range: [1, 25] },
        hard: { name: 'Champion (1-50)', range: [1, 50] }
      },
      is_premium: false
    },
    {
      id: 'digital_abacus',
      name: 'Digital Abacus',
      game_type: 'digital_abacus',
      description: 'Master the ancient art of the abacus! Learn to calculate using a Soroban.',
      difficulty_levels: {
        easy: { name: 'Beginner (1-20)', numRods: 2, maxNumber: 20, operations: ['represent'], challengeCount: 10, timeLimit: null },
        medium: { name: 'Intermediate (1-100)', numRods: 3, maxNumber: 100, operations: ['represent', '+'], challengeCount: 10, timeLimit: 180 },
        hard: { name: 'Advanced (1-9999)', numRods: 4, maxNumber: 9999, operations: ['represent', '+', '-', '×'], challengeCount: 10, timeLimit: 180 }
      },
      is_premium: false
    }
  ];

  const fetchAvailableGames = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/mental-math/games`);

      if (response.ok) {
        const data = await response.json();
        const backendGames = data.games || [];
        console.log('🔍 Games from backend API:', backendGames);

        // Merge: start with defaults, override with backend data where available
        const defaults = getDefaultGames();
        const backendMap = {};
        backendGames.forEach(g => { backendMap[g.game_type || g.id] = g; });

        const merged = defaults.map(def => {
          const backend = backendMap[def.game_type] || backendMap[def.id];
          return backend ? { ...def, ...backend, difficulty_levels: backend.difficulty_levels || def.difficulty_levels } : def;
        });
        // Also include any backend-only games not in defaults
        backendGames.forEach(g => {
          const key = g.game_type || g.id;
          if (!defaults.find(d => d.game_type === key || d.id === key)) {
            merged.push(g);
          }
        });

        console.log('🔍 Merged games:', merged);
        setAvailableGames(merged);
      } else {
        console.warn('Mental Math API not available, using default games');
        setAvailableGames(getDefaultGames());
      }
    } catch (error) {
      console.error('Error fetching games:', error);
      console.warn('Falling back to default games');
      setAvailableGames(getDefaultGames());
    } finally {
      setLoading(false);
    }
  };

  const handleGameSelect = async (game, difficulty) => {
    setSelectedGame({ ...game, difficulty });
    
    try {
      // Start game session
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/mental-math/sessions/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: user?.id,
          game_id: game.id,
          difficulty: difficulty,
          grade_level: userProfile?.grade  // NEW: Pass user's grade for enhanced experience
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setGameSession({
          session_id: data.session_id,
          game: game,
          difficulty: difficulty
        });
        setCurrentView('game');
      } else {
        console.warn('Backend unavailable, starting game in offline mode');
        setGameSession({
          session_id: 'offline_' + Date.now(),
          game: game,
          difficulty: difficulty,
          grade_level: userProfile?.grade,  // Include grade in offline mode too
          offline: true
        });
        setCurrentView('game');
      }
    } catch (error) {
      console.error('Error starting game session:', error);
      // Continue with offline mode
      setGameSession({
        session_id: 'offline_' + Date.now(),
        game: game,
        difficulty: difficulty,
        grade_level: userProfile?.grade,  // Include grade in fallback offline mode
        offline: true
      });
      setCurrentView('game');
    }
  };

  const handleGameComplete = async (results) => {
    setGameResults(results);
    
    console.log('Game completed! Session:', gameSession);
    console.log('Results:', results);
    
    // Save results to backend if online
    if (gameSession && !gameSession.offline) {
      console.log('Attempting to save results to backend...');
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
        const response = await fetch(`${backendUrl}/api/mental-math/sessions/complete`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            session_id: gameSession.session_id,
            user_id: user?.id,
            score: results.score,
            time_taken: results.timeTaken,
            questions_attempted: results.questionsAttempted,
            correct_answers: results.correctAnswers,
            game_data: results.gameData
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('✅ Game results saved successfully:', data);
        } else {
          console.error('❌ Failed to save game results. Status:', response.status);
          const errorText = await response.text();
          console.error('Error details:', errorText);
        }
      } catch (error) {
        console.error('❌ Error saving game results:', error);
      }
    } else {
      console.warn('🔄 Game session is offline, attempting direct leaderboard save...');
      
      // Try to save directly to leaderboard even if session was offline
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
        const response = await fetch(`${backendUrl}/api/mental-math/leaderboards/save`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            user_id: user?.id,
            game_id: gameSession?.game?.id,
            score: results.score,
            time_taken: results.timeTaken,
            questions_attempted: results.questionsAttempted,
            correct_answers: results.correctAnswers
          })
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('✅ Direct leaderboard save successful:', data);
        } else {
          console.error('❌ Direct leaderboard save failed. Status:', response.status);
        }
      } catch (error) {
        console.error('❌ Error with direct leaderboard save:', error);
      }
    }
    
    setCurrentView('results');
  };

  const handleBackToSelector = () => {
    setCurrentView('selector');
    setSelectedGame(null);
    setGameSession(null);
    setGameResults(null);
  };

  // Leaderboard functionality removed - keeping backend saving for future implementation

  const renderCurrentView = () => {
    console.log('Current view:', currentView, 'Selected game:', selectedGame?.game_type);
    switch (currentView) {
      case 'selector':
        return (
          <GameSelector
            games={availableGames}
            onGameSelect={handleGameSelect}
            loading={loading}
          />
        );
      
      case 'game':
        if (selectedGame?.game_type === 'speed_arithmetic') {
          return (
            <SpeedArithmeticGame
              gameSession={gameSession}
              onGameComplete={handleGameComplete}
              onBack={handleBackToSelector}
            />
          );
        } else if (selectedGame?.game_type === 'pattern_recognition' || selectedGame?.game_type === 'number_patterns') {
          return (
            <PatternRecognitionGame
              gameSession={gameSession}
              onGameComplete={handleGameComplete}
              onBack={handleBackToSelector}
            />
          );
        } else if (selectedGame?.game_type === 'math_battle') {
          return (
            <MathBattleGame
              gameSession={gameSession}
              onGameComplete={handleGameComplete}
              onBack={handleBackToSelector}
            />
          );
        } else if (selectedGame?.game_type === 'digital_abacus') {
          return (
            <AbacusGame
              gameSession={gameSession}
              onGameComplete={handleGameComplete}
              onBack={handleBackToSelector}
            />
          );
        } else {
          // Fallback for unknown game types
          return (
            <div className="text-center py-8">
              <h2 className="text-2xl font-bold text-gray-700 mb-4">Game Not Available</h2>
              <p className="text-gray-600 mb-6">
                Selected game type "{selectedGame?.game_type}" is not supported yet.
              </p>
              <button
                onClick={handleBackToSelector}
                className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-6 rounded-lg font-medium transition-colors"
              >
                Back to Games
              </button>
            </div>
          );
        }
      
      case 'results':
        return (
          <GameResults
            results={gameResults}
            game={selectedGame}
            onPlayAgain={() => setCurrentView('selector')}
            onBackToGames={handleBackToSelector}
          />
        );
      
      // Leaderboard view removed - will be added back when we have user base
      
      default:
        return <div>Loading...</div>;
    }
  };

  return (
    <div className="bg-gray-50 h-screen flex flex-col overflow-hidden">
      {/* Universal Header */}
      <UniversalHeader
        currentPage="mental-math"
        onBackToDashboard={onBackToDashboard}
        breadcrumbs={[
          { label: 'Dashboard', action: onBackToDashboard, current: false },
          { label: '🧠 Mental Math Games', action: null, current: true }
        ]}
      />

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {renderCurrentView()}
        </div>
      </div>
    </div>
  );
};

export default MentalMathSection;